<!--
  COD8 · Affitto mezzi — resoconto mensile per fornitore.
  Mese, KPI, segmenti Da saldare / Saldati, tabella per
  fornitore con dettaglio espandibile dei singoli noleggi.

  È il COD meglio servito dal backend: `GET /vehicle-rental/report?year=&month=`
  restituisce già righe per fornitore e totali calcolati lato server, quindi
  tutte e quattro le KPI sono reali. Il dettaglio dei noleggi arriva da
  `/vehicle-rental` (join `supplier` e `booking` consentiti).

  ⚠️ IL SALDO È IRREVERSIBILE. `PATCH /vehicle-rental/:id/pay` porta lo stato da
  `to_pay` a `paid` e il controller non espone alcun modo per tornare indietro.
  Per questo l'azione chiede conferma e dice quanti noleggi toccherà e per
  che importo.

  Nota: l'azione è per FORNITORE, l'endpoint per singolo noleggio — si salda
  quindi ciclando sui noleggi non ancora pagati di quel fornitore. Se qualcuno
  fallisce a metà, viene detto esattamente quanti sono passati.

  Il dettaglio non mostra cliente e percorso: `booking.customer` non è fra i
  join consentiti e il percorso sta sulle gambe escort, non raggiungibili da
  qui.
-->
<template>
  <div
    v-loading="loading"
    class="c8"
  >
    <div class="c8__head">
      <button
        type="button"
        class="c8__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="c8__title">
          <span class="c8__code">COD8</span> Affitto mezzi
        </h1>
        <p class="c8__sub">
          Mezzi affittati dai fornitori per i Transfer — solo costi, saldo a fine mese
        </p>
      </div>
    </div>

    <div class="c8__filters">
      <div class="field">
        <label>Mese</label>
        <div class="period">
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(-1)"
          >
            ‹
          </button>
          <span class="period__lbl">{{ periodLabel }}</span>
          <button
            type="button"
            class="period__nav"
            @click="shiftMonth(1)"
          >
            ›
          </button>
        </div>
      </div>
      <div class="c8__sp" />
      <el-input
        v-model="search"
        placeholder="Fornitore…"
        clearable
        style="width: 210px"
      />
    </div>

    <div class="kpis">
      <div class="kpi kpi--attn">
        <div class="kpi__lbl">
          Da saldare
        </div>
        <div class="kpi__num">
          {{ euro(totals.to_pay) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Saldato
        </div>
        <div class="kpi__num">
          {{ euro(totals.paid) }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Noleggi
        </div>
        <div class="kpi__num">
          {{ totals.count }}
        </div>
      </div>
      <div class="kpi">
        <div class="kpi__lbl">
          Fornitori
        </div>
        <div class="kpi__num">
          {{ rows.length }}
        </div>
      </div>
    </div>

    <div class="board">
      <div class="toolbar">
        <div class="segs">
          <button
            v-for="s in SEGMENTS"
            :key="s.key"
            type="button"
            class="seg"
            :class="{ 'is-active': segment === s.key }"
            @click="segment = s.key"
          >
            {{ s.label }}
            <span class="seg__c">{{ countOf(s.key) }}</span>
          </button>
        </div>
      </div>

      <el-table
        :data="filtered"
        style="width: 100%"
        row-key="supplier_id"
        :empty-text="segment === 'dasald' && countOf('sald')
          ? `Niente da saldare: ${countOf('sald')} ${countOf('sald') === 1 ? 'fornitore già saldato' : 'fornitori già saldati'}, nella scheda Saldati`
          : 'Nessun noleggio per il periodo selezionato'"
        @expand-change="onExpand"
      >
        <el-table-column type="expand">
          <template #default="scope">
            <div class="detail">
              <div
                v-if="loadingRentals[scope.row.supplier_id]"
                class="detail__wait"
              >
                Caricamento noleggi…
              </div>
              <div
                v-else-if="truncatedRentals[scope.row.supplier_id]"
                class="detail__wait"
              >
                ⚠ Il server ha troncato l'elenco: questi noleggi non sono
                tutti quelli del mese. Restringi il periodo.
              </div>
              <el-table
                v-else
                :data="rentals[scope.row.supplier_id] || []"
                size="small"
                style="width: 100%"
                empty-text="Nessun noleggio"
              >
                <el-table-column
                  label="Data"
                  width="120"
                >
                  <template #default="r">
                    {{ fmtDate(r.row.rental_date) }}
                  </template>
                </el-table-column>
                <el-table-column
                  label="Booking"
                  min-width="180"
                >
                  <template #default="r">
                    <button
                      v-if="r.row.booking?.id"
                      type="button"
                      class="lnk"
                      @click="openBooking(r.row.booking.id)"
                    >
                      {{ shortId(r.row.booking.id) }}
                    </button>
                    <span
                      v-else
                      class="muted"
                    >—</span>
                  </template>
                </el-table-column>
                <el-table-column
                  label="Mezzo"
                  min-width="150"
                >
                  <template #default="r">
                    <span class="mezzo">{{ vehicleLabel(r.row.vehicle_type) }}</span>
                  </template>
                </el-table-column>
                <el-table-column
                  label="Stato"
                  width="120"
                >
                  <template #default="r">
                    <StatusPill
                      :kind="r.row.status === 'paid' ? 'ok' : 'attn'"
                      :label="r.row.status === 'paid' ? 'Saldato' : 'Da saldare'"
                    />
                  </template>
                </el-table-column>
                <el-table-column
                  label="Costo"
                  width="120"
                  align="right"
                >
                  <template #default="r">
                    <span class="val">{{ euro(r.row.cost) }}</span>
                  </template>
                </el-table-column>
                <el-table-column
                  width="110"
                  align="right"
                >
                  <template #default="r">
                    <el-button
                      v-if="r.row.status !== 'paid'"
                      size="small"
                      :loading="paying === r.row.id"
                      @click="confirmPayOne(scope.row, r.row)"
                    >
                      Salda
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>

              <p class="detail__note">
                Cliente e percorso non sono disponibili da qui:
                <code>booking.customer</code> non è fra i join consentiti e il
                percorso sta sulle gambe del Transfer.
              </p>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Fornitore"
          min-width="240"
        >
          <template #default="scope">
            <div class="forn">
              <span class="forn__ic">🚐</span>
              <span class="forn__nm">{{ scope.row.supplier }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Noleggi"
          width="110"
          align="right"
        >
          <template #default="scope">
            <span class="val">{{ scope.row.count }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Da saldare"
          width="140"
          align="right"
        >
          <template #default="scope">
            <span
              class="val"
              :class="{ 'val--attn': scope.row.to_pay > 0 }"
            >{{ euro(scope.row.to_pay) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Saldato"
          width="130"
          align="right"
        >
          <template #default="scope">
            <span class="val">{{ euro(scope.row.paid) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Totale costo"
          width="140"
          align="right"
        >
          <template #default="scope">
            <span class="val val--tot">{{ euro(scope.row.total) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Azioni"
          width="170"
          align="right"
          fixed="right"
        >
          <template #default="scope">
            <el-button
              v-if="scope.row.to_pay > 0"
              size="small"
              type="success"
              :loading="paying === scope.row.supplier_id"
              @click.stop="confirmPay(scope.row)"
            >
              Segna saldato
            </el-button>
            <StatusPill
              v-else
              kind="ok"
              label="Tutto saldato"
            />
          </template>
        </el-table-column>
      </el-table>

      <div class="board__foot">
        <span>{{ filtered.length }} fornitori</span>
        <span class="c8__sp" />
        <span class="board__tot">
          Totale periodo <b>{{ euro(totals.total) }}</b>
        </span>
      </div>
    </div>

    <p class="c8__note">
      Qui non ci sono ricavi: il prezzo del Transfer al cliente sta sul lato
      ricavi. Il saldo di un noleggio <b>non è reversibile</b> dall'applicazione.
    </p>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import contabilitaAPI from '../../../services/contabilitaAPI.js';
import { periodFromRoute, syncPeriodToUrl } from '../../../services/period.js';
import StatusPill from '../../../components/ui/StatusPill.vue';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const SEGMENTS = [
  { key: 'dasald', label: 'Da saldare' },
  { key: 'sald', label: 'Saldati' },
];

// I valori dell'enum `vehicle_type` sul booking, in italiano.
const VEHICLES = {
  auto_partner: 'Auto partner',
  auto_nostra: 'Auto nostra',
  pulmino_9: 'Pulmino 9 posti',
  navetta_30: 'Navetta 30 posti',
  navetta_50: 'Navetta 50 posti',
};

export default {
  name: 'Cod8Page',
  components: { StatusPill },

  setup() {
    const route = useRoute();
    const router = useRouter();
    // Il periodo arriva dalla URL (?y=&m=), non dal calendario: chi entra
    // dall'hub deve restare sul mese che stava guardando. Senza query valida
    // si ricade sul mese corrente.
    const periodo = periodFromRoute(route);
    const year = ref(periodo.year);
    const month = ref(periodo.month);

    const rows = ref([]);
    const totals = ref({ count: 0, total: 0, to_pay: 0, paid: 0 });
    const loading = ref(false);
    const search = ref('');
    const segment = ref('dasald');
    const paying = ref('');

    // dettaglio per fornitore, caricato solo all'apertura della riga
    const rentals = reactive({});
    const loadingRentals = reactive({});
    // per fornitore: l'elenco dei noleggi è stato troncato dal server?
    const truncatedRentals = reactive({});

    const periodLabel = computed(() => `${MONTHS[month.value - 1]} ${year.value}`);

    const bySegment = (key) =>
      rows.value.filter((r) =>
        key === 'dasald' ? Number(r.to_pay) > 0 : Number(r.to_pay) === 0
      );

    const countOf = (key) => bySegment(key).length;

    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase();
      return bySegment(segment.value).filter(
        (r) => !q || (r.supplier || '').toLowerCase().includes(q)
      );
    });

    const load = async () => {
      loading.value = true;
      try {
        const res = await contabilitaAPI.cod8(year.value, month.value);
        rows.value = res.rows;
        totals.value = res.totals;
        // il dettaglio già aperto va ricaricato: cambia il periodo
        for (const k of Object.keys(rentals)) delete rentals[k];
      } catch (e) {
        console.error('Caricamento COD8 fallito:', e);
        rows.value = [];
        totals.value = { count: 0, total: 0, to_pay: 0, paid: 0 };
      } finally {
        loading.value = false;
      }
    };

    const onExpand = async (row, expandedRows) => {
      const open = expandedRows.some((r) => r.supplier_id === row.supplier_id);
      if (!open || rentals[row.supplier_id]) return;
      loadingRentals[row.supplier_id] = true;
      try {
        const res = await contabilitaAPI.cod8Rentals(
          row.supplier_id, year.value, month.value
        );
        rentals[row.supplier_id] = res.rows;
        // elenco troncato: va detto, altrimenti dei noleggi sparirebbero
        // dalla vista senza che nessuno se ne accorga
        truncatedRentals[row.supplier_id] = res.truncated;
      } catch (e) {
        console.error('Caricamento noleggi fallito:', e);
        rentals[row.supplier_id] = [];
      } finally {
        loadingRentals[row.supplier_id] = false;
      }
    };

    // L'endpoint salda UN noleggio per volta e l'operazione non è reversibile:
    // si dice quanti e per quanto prima di procedere.
    const confirmPay = async (row) => {
      let list = rentals[row.supplier_id];
      if (!list) {
        const res = await contabilitaAPI.cod8Rentals(row.supplier_id, year.value, month.value);
        list = res.rows;
        rentals[row.supplier_id] = list;
        truncatedRentals[row.supplier_id] = res.truncated;
      }
      const unpaid = list.filter((r) => r.status !== 'paid');
      if (!unpaid.length) {
        ElMessage.info('Non ci sono noleggi da saldare per questo fornitore');
        return;
      }

      try {
        await ElMessageBox.confirm(
          `Verranno segnati come saldati ${unpaid.length} noleggi di ` +
            `${row.supplier}, per ${euro(row.to_pay)}. L'operazione non è ` +
            'reversibile dall\'applicazione.',
          'Confermi il saldo?',
          { confirmButtonText: 'Segna saldato', cancelButtonText: 'Annulla', type: 'warning' }
        );
      } catch {
        return; // annullato
      }

      paying.value = row.supplier_id;
      const res = await Promise.allSettled(
        unpaid.map((r) => contabilitaAPI.cod8Pay(r.id))
      );
      paying.value = '';

      const ko = res.filter((r) => r.status === 'rejected').length;
      if (ko) {
        ElMessage.error(
          `Saldati ${res.length - ko} noleggi su ${res.length}: ${ko} non sono riusciti.`
        );
      } else {
        ElMessage.success(`${res.length} noleggi segnati come saldati`);
      }
      delete rentals[row.supplier_id];
      await load();
    };

    // Un solo noleggio: quando la fattura del fornitore ne copre solo una parte.
    const confirmPayOne = async (supplier, rental) => {
      try {
        await ElMessageBox.confirm(
          `Il noleggio del ${fmtDate(rental.rental_date)} di ${supplier.supplier}, ` +
            `${euro(rental.cost)}, verrà segnato come saldato. L'operazione non è ` +
            'reversibile dall\'applicazione.',
          'Confermi il saldo?',
          { confirmButtonText: 'Segna saldato', cancelButtonText: 'Annulla', type: 'warning' }
        );
      } catch {
        return; // annullato
      }
      paying.value = rental.id;
      try {
        await contabilitaAPI.cod8Pay(rental.id);
        ElMessage.success('Noleggio segnato come saldato');
      } catch (e) {
        console.error('Saldo noleggio fallito:', e);
        ElMessage.error('Saldo non riuscito: riprova');
      } finally {
        paying.value = '';
      }
      await load();
      // il dettaglio resta aperto: si ricaricano i noleggi del fornitore
      try {
        const res = await contabilitaAPI.cod8Rentals(supplier.supplier_id, year.value, month.value);
        rentals[supplier.supplier_id] = res.rows;
        truncatedRentals[supplier.supplier_id] = res.truncated;
      } catch (e) {
        delete rentals[supplier.supplier_id];
      }
    };

    const shiftMonth = (delta) => {
      let m = month.value + delta;
      let y = year.value;
      if (m < 1) { m = 12; y -= 1; }
      if (m > 12) { m = 1; y += 1; }
      month.value = m;
      year.value = y;
      // la URL segue il mese mostrato: un refresh non riporta a oggi
      syncPeriodToUrl(router, route, y, m);
      load();
    };

    function euro(n) {
      return `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
    }

    const fmtDate = (d) =>
      d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '—';

    const shortId = (id) => `#${String(id).slice(0, 8)}`;
    const vehicleLabel = (v) => VEHICLES[v] || v || '—';

    const openBooking = (id) => router.push(`/booking/detail/${id}`);
    const goBack = () => router.push('/contabilita');

    onMounted(load);

    return {
      year, month, rows, totals, loading, search, segment, paying,
      rentals, loadingRentals, truncatedRentals, filtered, periodLabel, SEGMENTS,
      countOf, onExpand, confirmPay, confirmPayOne, shiftMonth,
      euro, fmtDate, shortId, vehicleLabel, openBooking, goBack,
    };
  },
};
</script>

<style scoped>
.c8 { padding: 4px 0 32px; }
.c8__sp { flex: 1; }

.c8__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.c8__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.c8__back:hover { border-color: var(--brand-violet); }
.c8__title {
  margin: 0; font-size: 21px; display: flex; align-items: center; gap: 10px;
  color: var(--text-primary);
}
.c8__code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; font-weight: 700; color: #fff; background: #a62e82;
  padding: 3px 8px; border-radius: 6px;
}
.c8__sub { margin: 3px 0 0; font-size: 13px; color: var(--text-tertiary); }

.c8__filters {
  display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 14px;
}
.field {
  display: flex; align-items: center; gap: 8px; padding: 5px 11px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: 10px;
}
.field label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700;
}
.period { display: flex; align-items: center; gap: 2px; }
.period__nav {
  width: 30px; height: 30px; border: 0; background: transparent;
  border-radius: 7px; cursor: pointer; font-size: 15px;
  color: var(--text-secondary); font-family: inherit;
}
.period__nav:hover { background: var(--bg-tertiary); }
.period__lbl {
  padding: 0 8px; font-weight: 700; font-variant-numeric: tabular-nums;
  min-width: 118px; text-align: center; color: var(--text-primary);
}

.kpis {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 16px;
}
@media (max-width: 820px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
.kpi {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); padding: 13px 15px;
}
.kpi__lbl {
  font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 600;
}
.kpi__num {
  font-size: 22px; font-weight: 750; letter-spacing: -0.02em; margin-top: 4px;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.kpi--attn .kpi__num { color: var(--state-attn); }

.board {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.toolbar {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-bottom: 1px solid var(--border-color);
}
.segs { display: flex; gap: 5px; }
.seg {
  border: 1px solid transparent; background: transparent; cursor: pointer;
  color: var(--text-secondary); padding: 7px 12px; border-radius: 8px;
  font-size: 13px; font-weight: 600; font-family: inherit;
  display: inline-flex; align-items: center; gap: 8px;
}
.seg:hover { background: var(--bg-tertiary); }
.seg.is-active {
  background: var(--svc-fleet-bg); color: var(--brand-violet);
  border-color: var(--brand-violet);
}
.seg__c {
  font-size: 11.5px; font-weight: 700; border-radius: 20px; padding: 1px 8px;
  background: var(--state-muted-bg); color: var(--state-muted);
}
.seg.is-active .seg__c { background: var(--brand-violet); color: #fff; }

.forn { display: flex; align-items: center; gap: 9px; }
.forn__ic {
  width: 30px; height: 30px; flex: none; border-radius: 8px;
  background: #a62e82; display: grid; place-items: center; font-size: 14px;
}
.forn__nm { font-weight: 600; color: var(--text-primary); }

.val { font-variant-numeric: tabular-nums; font-weight: 700; }
.val--attn { color: var(--state-attn); }
.val--tot { color: var(--text-primary); }
.muted { color: var(--text-tertiary); }
.mezzo { font-weight: 600; color: var(--text-primary); }
.lnk {
  background: none; border: 0; padding: 0; cursor: pointer; font-family: inherit;
  color: var(--brand-violet); font-weight: 600; font-size: 12.5px;
}
.lnk:hover { text-decoration: underline; }

.detail { padding: 14px 16px; background: var(--bg-secondary); }
.detail__wait { font-size: 13px; color: var(--text-tertiary); padding: 8px 0; }
.detail__note {
  margin: 10px 0 0; font-size: 11.5px; color: var(--text-tertiary); line-height: 1.5;
}
.detail__note code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px;
  background: var(--bg-tertiary); padding: 0 4px; border-radius: 4px;
}

.board__foot {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-top: 1px solid var(--border-color);
  font-size: 12.5px; color: var(--text-tertiary);
}
.board__tot { color: var(--text-secondary); }
.board__tot b {
  color: var(--text-primary); font-variant-numeric: tabular-nums; font-size: 14px;
}

.c8__note {
  margin: 12px 0 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5;
}

.c8 :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 10px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
